import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import maplibregl from 'maplibre-gl';
import {cogProtocol} from '@geomatico/maplibre-cog-protocol';
import { Map, NavigationControl, Source, Layer, MapRef, ViewStateChangeEvent, Marker } from 'react-map-gl/maplibre';

import 'maplibre-gl/dist/maplibre-gl.css';
import {  CountryBasicInfo, PointOfInterest } from '../types';
import '../styles/earthdaily-theme.css';
import { cropColorMap } from '../utils/colorMap';
import { fetchCountryData } from '../utils/dataLoader';
import { EnvironmentOutlined } from '@ant-design/icons';
maplibregl.addProtocol('cog', cogProtocol);

const ZOOM_LEVELS = {
  COUNTRIES: 4,
  POIS: 9,
  SATELLITE: 11
} as const;

interface MapGlobeProps {
  countriesData: CountryBasicInfo[];
  activeSeason?: string | null;
  onZoomChange?: (zoom: number) => void;
  onActivePOIChange?: (poiName: string | null) => void;
  onActiveCountryChange?: (countryName: string | null) => void;
  onVisibleLevelChange?: (level: 'countries' | 'pois' | 'features') => void;
  onActiveSeasonChange?: (season: string | null) => void;
  onCropHover?: (cropType: string | null) => void;
}

interface ViewState {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
  projection?: string;
}

const MapGlobe = forwardRef<any, MapGlobeProps>((
  {
    countriesData,
    activeSeason,
    onZoomChange,
    onActivePOIChange,
    onActiveCountryChange,
    onVisibleLevelChange,
    onActiveSeasonChange,
    onCropHover
  },
  ref
) => {
  const mapRef = useRef<MapRef>(null);
  const mapLoadedRef = useRef(false);
  const [viewState, setViewState] = useState<ViewState>({
    longitude: -60,
    latitude: 28,
    zoom: 2.8,
    pitch: 0,
    bearing: 0,
    projection: 'globe'
  });

  const [allCountriesData, setAllCountriesData] = useState<Record<string, any>>({});
  const [currentCenter, setCurrentCenter] = useState<[number, number]>([-60, 28]);
  const [currentZoom, setCurrentZoom] = useState(2.8);
  const [cropFilters, setCropFilters] = useState<string[]>([]);
  const [hoveredCropType, setHoveredCropType] = useState<string | null>(null);
  const countryDataLoadedRef = useRef<Record<string, boolean>>({});

  const getZoomLevel = (zoom: number): 'countries' | 'pois' | 'features' => {
    if (zoom < ZOOM_LEVELS.COUNTRIES) return 'countries';
    if (zoom < ZOOM_LEVELS.POIS) return 'pois';
    return 'features';
  };

  const findClosest = <T extends { center?: number[], coordinates?: number[] }>(
    items: T[], 
    center: number[]
  ): T | null => {
    return items.reduce((closest, item) => {
      const itemCenter = item.center || item.coordinates;
      if (!itemCenter) return closest;
      
      const distance = Math.hypot(center[0] - itemCenter[0], center[1] - itemCenter[1]);
      return !closest || distance < closest.distance 
        ? { ...item, distance } 
        : closest;
    }, null as (T & { distance: number }) | null);
  };

  const loadCountryData = async (countryName: string): Promise<void> => {
    if (countryDataLoadedRef.current[countryName]) return;

    try {
      const { countryData, fullGeoJSON: geoJSON } = await fetchCountryData(countryName);
      if (geoJSON) {
        setAllCountriesData(prev => ({
          ...prev,
          [countryName]: { countryData, geoJSON }
        }));
        countryDataLoadedRef.current[countryName] = true;
      }
    } catch (error) {
      console.error("Erreur lors du chargement du GeoJSON:", error);
    }
  };

  // Charger les données des pays proches quand on zoom
  useEffect(() => {
    const level = getZoomLevel(currentZoom);
    if (level !== 'countries') {
      const closestCountry = findClosest(countriesData, currentCenter) as CountryBasicInfo | null;
      if (closestCountry) {
        loadCountryData(closestCountry.name);
      }
    }
  }, [currentZoom, currentCenter, countriesData]);

  const handleCountryMarkerClick = (countryName: string) => {
    const country = countriesData.find(c => c.name === countryName);
    if (country && mapRef.current) {
      mapRef.current.flyTo({
        center: country.center,
        zoom: ZOOM_LEVELS.COUNTRIES + 1,
        duration: 1500,
        essential: true
      });
    }
  };

  const handlePOIMarkerClick = (poiName: string) => {
    const currentCountry = findClosest(countriesData, currentCenter) as CountryBasicInfo | null;
    if (!currentCountry || !allCountriesData[currentCountry.name]) return;
    
    const poi = allCountriesData[currentCountry.name].countryData.pointsOfInterest.find((p: any) => p.name === poiName);
    if (poi && mapRef.current) {
      mapRef.current.flyTo({
        center: poi.coordinates,
        zoom: ZOOM_LEVELS.POIS + 2,
        duration: 1200,
        essential: true
      });
    }
  };

  const handleSeasonChange = (seasonId: string) => {
    // Logique simplifiée pour changer de saison
    onActiveSeasonChange?.(seasonId);
  };

  const handleCropFilterChange = (cropTypes: string[]) => {
    setCropFilters(cropTypes);
  };

  const handleCropHover = (cropType: string | null) => {
    setHoveredCropType(cropType);
    onCropHover?.(cropType);
  };

  const resetToGlobalView = () => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [-60, 28],
        zoom: 2.8,
        duration: 1500,
        essential: true
      });
    }
  };

  useImperativeHandle(ref, () => ({
    handleCountryMarkerClick,
    handlePOIMarkerClick,
    handleSeasonChange,
    handleCropFilterChange,
    handleCropHover,
    resetToGlobalView
  }));

  const handleViewStateChange = (e: ViewStateChangeEvent) => {
    setViewState(e.viewState);
    setCurrentZoom(e.viewState.zoom);
    setCurrentCenter([e.viewState.longitude, e.viewState.latitude]);
    
    const newLevel = getZoomLevel(e.viewState.zoom);
    const center = [e.viewState.longitude, e.viewState.latitude];

    // Notifier les changements
    onVisibleLevelChange?.(newLevel);
    onZoomChange?.(e.viewState.zoom);

    // Déterminer les éléments actifs basés sur la position
    if (newLevel === 'pois' || newLevel === 'features') {
      const closestCountry = findClosest(countriesData, center) as CountryBasicInfo | null;
      onActiveCountryChange?.(closestCountry?.name || null);
      
      if (newLevel === 'features' && closestCountry && allCountriesData[closestCountry.name]) {
        const pois = allCountriesData[closestCountry.name].countryData.pointsOfInterest;
        const closestPOI = findClosest(pois, center) as PointOfInterest | null;
        if (closestPOI) {
          onActivePOIChange?.(closestPOI.name);
          // Ne pas changer la saison si une saison est déjà sélectionnée
          if (!activeSeason && closestPOI.seasons?.[0]) {
            onActiveSeasonChange?.(closestPOI.seasons[0].id);
          }
        }
      } else {
        onActivePOIChange?.(null);
        onActiveSeasonChange?.(null);
      }
    } else {
      onActiveCountryChange?.(null);
      onActivePOIChange?.(null);
      onActiveSeasonChange?.(null);
    }
  };

  // Obtenir les données à afficher basées sur la position actuelle
  const getCurrentCountryData = () => {
    const level = getZoomLevel(currentZoom);
    if (level === 'countries') return null;
    
    const closestCountry = findClosest(countriesData, currentCenter) as CountryBasicInfo | null;
    return closestCountry && allCountriesData[closestCountry.name] ? allCountriesData[closestCountry.name] : null;
  };

  const getCurrentPOI = () => {
    const level = getZoomLevel(currentZoom);
    if (level !== 'features') return null;
    
    const countryData = getCurrentCountryData();
    if (!countryData) return null;
    
    return findClosest(countryData.countryData.pointsOfInterest, currentCenter) as PointOfInterest | null;
  };

  const getCropColorExpression = (): mapboxgl.Expression => {
    const matchExpression: any[] = ['match', ['get', 'type', ['properties']]];
    Object.entries(cropColorMap).forEach(([key, value]) => {
      matchExpression.push(key, value);
    });
    matchExpression.push('#666');
    return matchExpression as unknown as mapboxgl.Expression;
  };

  return (
    <Map
      {...viewState}
      onMove={handleViewStateChange}
      mapStyle="https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json"
      attributionControl={false}
      style={{ width: '100%', height: '100vh' }}
      ref={mapRef}
      projection="globe"
      reuseMaps={true}
      renderWorldCopies={true}
    >
      <NavigationControl position="top-right" />

      {/* Markers de pays - Niveau dézoomé */}
      {getZoomLevel(currentZoom) === 'countries' && countriesData.map(country => (
        <Marker
          key={country.name}
          longitude={country.center[0]}
          latitude={country.center[1]}
          anchor="bottom"
          onClick={() => handleCountryMarkerClick(country.name)}
        >
          <div style={{
            cursor: 'pointer',
            color: country.color,
            backgroundColor: 'white',
            padding: '4px',
            borderRadius: '50%',
            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
            border: `2px solid ${country.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '30px',
            height: '30px',
            fontSize: '16px'
          }}>
            <EnvironmentOutlined />
          </div>
          <div style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
            backgroundColor: 'rgba(255,255,255,0.8)',
            padding: '2px 5px',
            borderRadius: '3px',
            fontSize: '12px',
            fontWeight: 'bold',
            marginTop: '2px'
          }}>
            {country.name}
          </div>
        </Marker>
      ))}

      {/* Markers de POI - Niveau intermédiaire */}
      {(() => {
        const level = getZoomLevel(currentZoom);
        const countryData = getCurrentCountryData();
        return level === 'pois' && countryData ? countryData.countryData.pointsOfInterest.map((poi: any) => (
          <Marker
            key={poi.name}
            longitude={poi.coordinates[0]}
            latitude={poi.coordinates[1]}
            anchor="bottom"
            onClick={() => handlePOIMarkerClick(poi.name)}
          >
            <div style={{
              cursor: 'pointer',
              color: '#15b89a',
              backgroundColor: 'white',
              padding: '3px',
              borderRadius: '50%',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              border: '2px solid #15b89a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              fontSize: '14px'
            }}>
              <EnvironmentOutlined />
            </div>
            <div style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              whiteSpace: 'nowrap',
              backgroundColor: 'rgba(255,255,255,0.8)',
              padding: '2px 5px',
              borderRadius: '3px',
              fontSize: '11px',
              fontWeight: 'bold',
              marginTop: '2px'
            }}>
              {poi.name}
            </div>
          </Marker>
        )) : null;
      })()}

      {/* TIFF raster pour POI - Niveau zoomé */}
      {(() => {
        const level = getZoomLevel(currentZoom);
        const country = getCurrentCountryData();
        const poi = getCurrentPOI();
        
        if (level === 'features' && country && poi && activeSeason) {
          const poiId = poi.id || poi.id || poi.name;
          const tiffUrl = `cog:///data/tiff/${country.countryData.id}/${poiId}_${activeSeason}.tif`;

          return (
            <Source
              key={`${poiId}-${activeSeason}`}
              id="poi-tiff-source"
              type="raster"
              url={tiffUrl}
              tileSize={256}
            >
              <Layer
                id="poi-tiff-layer"
                type="raster"
            
                paint={{
                  'raster-opacity': 0.8
                }}
              />
            </Source>
          );
        }
        return null;
      })()}


      {/* Couche satellite */}
      {currentZoom >= ZOOM_LEVELS.SATELLITE && (
        <Source
          id="satellite-source"
          type="raster"
          tiles={[
            'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
          ]}
          tileSize={256}
        >
          <Layer
            id="satellite-layer"
            type="raster"
            source="satellite-source"
            paint={{
              'raster-opacity': 0.6,
              'raster-brightness-min': 0,
              'raster-brightness-max': 0.4,
              'raster-contrast': 0.2,
              'raster-saturation': -1,
              'raster-fade-duration': 0
            }}
            beforeId='poi-tiff-layer'
          />
        </Source>
      )}


    </Map>
  );
});

export default MapGlobe;